import React, {useEffect, useState} from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import apiClient from '../../../../utils/apiClient';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Alert, Snackbar} from '@mui/material';
import decodeToken from '../../../../utils/decode-token';
import Grid from '@mui/material/Grid2';
import RowActionsMenu from '../../common/table/row-actions-menu';
import {compareDmyDates, sortRows} from '../../../../utils/tableUtils';
import useSummarizedFilteredRows from '../../common/table/hooks/use-summarized-filtered-rows';
import handleEmptyTableResponse from '../../common/table/utils/handle-empty-table-response';

const SORT_COMPARATORS = {
  last_update: (a, b) => compareDmyDates(a.last_update, b.last_update),
};

function TableHacExited({reload, onDataLoaded, onSummaryChange}) {
  const {t} = useTranslation();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRowsWithSummary] = useSummarizedFilteredRows(onSummaryChange);
  const navigate = useNavigate();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [retailerOptions, setRetailerOptions] = useState([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState('');
  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;

  const userId = decodeToken(token).id;

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      const authToken = token;
      const url = `${process.env.REACT_APP_API_URL}/hac/retailer-salesperson?status=exited`;

      const config = {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Cache-Control': 'no-cache',
        },
      };

      try {
        const response = await apiClient.get(url, config);
        if (response.status === 200) {
          const sortedRows = sortRows(response.data, 'last_update', 'desc', SORT_COMPARATORS);

          const storedRetailerId = sessionStorage.getItem('filter-retailer');
          if (storedRetailerId) {
            const filtered = sortedRows.filter(row => row.retailer_info.id === Number(storedRetailerId));
            setFilteredRowsWithSummary(filtered);
            setSelectedRetailerId(storedRetailerId);
          } else {
            setFilteredRowsWithSummary(sortedRows);
          }

          setRows(sortedRows);
          onDataLoaded();
        } else if (response.status === 204) {
          handleEmptyTableResponse({
            clearStateSetters: [setRows, setFilteredRowsWithSummary],
            onDataLoaded,
          });
        }
      } catch (error) {
        if (error.response) {
          setSnackbarOpen(true);
          setSnackbarMessage(t('common-internal-server-error'), error);
        }
      } finally {
        setDataLoaded(true);
      }

      const userApiUrl = `${process.env.REACT_APP_API_URL}/retailer/staff-member?staffId=${userId}`;
      try {
        const userResponse = await apiClient.get(userApiUrl, config);
        setRetailerOptions(userResponse.data.retailerInfo || []);
      } catch (error) {
        console.error('Error al obtener datos del usuario:', error);
      }
    };

    if (reload) {
      fetchData();
    }
  }, [reload, token, onDataLoaded, t, userId, setFilteredRowsWithSummary]);

  return (
    <>
      {dataLoaded ? (
        rows && rows.length !== 0 ? (
          <div>
            <div className="intro">
              <div className="text">
                <h4>{t('retailer-main-exited-title')}</h4>
                <p>{t('retailer-main-exited-text')}</p>
              </div>
            </div>

            {retailerOptions.length > 1 && (
              <div className="filters">
                <p>{t('common-search')}</p>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 10, md: 4 }}>
                    <div className="form-control">
                      <label htmlFor="retailer">{t('common-retailer')}</label>
                      <select
                        name="retailer"
                        id="retailer"
                        className="normal"
                        value={selectedRetailerId}
                        onChange={(e) => setSelectedRetailerId(e.target.value)}
                      >
                        <option value="">{t('common-all-retailers')}</option>
                        {retailerOptions.map((retailer) => (
                          <option key={retailer.id} value={retailer.id}>
                            {retailer.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Grid>
                </Grid>
              </div>
            )}
            {filteredRows.length !== 0 ? (
              <div className="table-wrapper">
                <Table aria-label="simple table" className="user-table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">{t('common-name')}</TableCell>
                      <TableCell align="left">{t('common-last-name')}</TableCell>
                      <TableCell align="center">{t('common-phone')}</TableCell>
                      <TableCell align="center">{t('common-role-pma')}</TableCell>
                      {role === 'retailerGM' && (
                        <TableCell align="center">{t('common-retailer')}</TableCell>
                      )}
                      <TableCell align="center">{t('common-last-update')}</TableCell>
                      <TableCell align="center"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>

                    {filteredRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align="left">{row.name}</TableCell>
                        <TableCell align="left">{row.last_name}</TableCell>
                        <TableCell align="center">
                          {row.prefix_phone} {row.phone}
                        </TableCell>
                        <TableCell align="center">{row.pma}</TableCell>
                        {role === 'retailerGM' && (
                          <TableCell align="center">{row.retailer_info.name} ({row.retailer_info.city})</TableCell>
                        )}
                        <TableCell align="center">{row.last_update}</TableCell>
                        <TableCell align="right">
                          {row.status && row.status !== 'exited' ? (
                            <RowActionsMenu
                              actions={[
                                {
                                  key: 'profile',
                                  label: t('common-profile'),
                                  onClick: () => navigate(`/retailer/hac-profile/${row.id}`),
                                },
                                {
                                  key: 'vehicles',
                                  label: t('pma-hac-tab-option2'),
                                  onClick: () => navigate(`/common/vehicles/${row.id}`),
                                },
                                {
                                  key: 'preferences',
                                  label: t('pma-hac-tab-option3'),
                                  onClick: () => navigate(`/common/preferences/${row.id}`),
                                },
                              ]}
                            />

                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}

                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="no-result">
                <h2 className="txt-center">
                  <small>{t('admin-main-tab1-text-empty')}</small>
                </h2>
              </div>
            )}
          </div>
        ) : (
          <div className="no-result">
            <h2 className="txt-center">
              {t('retailer-main-exited-empty-title')}
              <small>{t('retailer-main-exited-empty-text')}</small>
            </h2>
          </div>
        )
      ) : (
        <div>{t('common-loading')}</div>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity="error" sx={{width: '100%'}}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default TableHacExited;
